import { InputSelect } from '../../components/forms/inputs/InputSelect';
import { Button } from '../../components/buttons/Button';
import { useEffect, useState, useRef } from "react";
import { postData, useGetData } from "../../services/api/api-tools";
import LogoPerception from '../../assets/images/logos/logo-blue-black.png';
//import LogoClient from '../../assets/images/logos/logo-client.png';
import { useSocket } from "../../contexts/SocketContext";
import {
  ArrowPathIcon,
  BookOpenIcon,
  CheckCircleIcon,
  CheckIcon,
  ExclamationCircleIcon,
  ExclamationTriangleIcon,
  XMarkIcon
} from "@heroicons/react/20/solid";
import { useAlert } from "../../contexts/AlertContext";
import { GeneralTimeline } from "./components/GeneralTimeline";
import { ActionButton } from "../../components/buttons/ActionButton";
import Badge from "../../components/badges/Badge";


export const DemoLivePage = () => {

  const SHOW_RESTART_BUTTON = true;
  const phasesText = ['Rien', 'Montre positionnée', 'Vissage', 'Nettoyage'];

  const AI_MESSAGES = {
    126: [
      'Place the rotor carefully to ensure free and smooth rotation.',
      'Verify proper alignment with the central axis before securing.',
      'Avoid applying pressure on delicate bearings during installation.',
    ],
    1001: [
      'Insert the screw straight to prevent cross-threading.',
      'Start tightening gently to ensure proper engagement.',
      'Use the correct tool size to avoid damaging the screw head.',
    ],
    1002: [
      'Make sure all screws are fully seated without over-tightening.',
      'Check alignment before tightening to avoid stressing the parts.',
      'Use consistent torque to keep the assembly stable.',
    ],
    1003: [
      'Ensure the gasket is clean and free from any debris before placement.',
      'Position the gasket evenly to guarantee proper sealing.',
      'Avoid stretching or twisting the gasket during installation.',
    ],
    1004: [
      'Align the cover correctly with the case before pressing it into place.',
      'Apply even pressure to secure the cover without distortion.',
      'Check that the seal is properly seated to maintain water resistance.',
    ],
    2000: [
      'Vérifiez que le joint est propre avant la pose.',
      'Positionnez le joint uniformément dans son logement sans le vriller.',
      'Assurez-vous que le joint est correctement engagé sur toute sa périphérie.',
    ],
    2001: [
      'Alignez soigneusement la pièce jaune avec les points de référence de l’assemblage.',
      'Maintenez la pièce en position pour éviter tout déplacement pendant l’opération.',
      'Contrôlez que la pièce est parfaitement plaquée avant de poursuivre le montage.',
    ],
    2002: [
      'Insérez les deux vis manuellement pour garantir un bon engagement du filetage.',
      'Positionnez les vis bien droites afin d’éviter tout risque de croisement de filets.',
      'Vérifiez que les vis sont correctement engagées avant le vissage définitif.',
    ],
    2003: [
      'Maintenez le tournevis perpendiculairement à la surface pendant le vissage.',
      'Appliquez le couple de serrage recommandé pour garantir la qualité de l’assemblage.',
      'Contrôlez que les vis sont complètement serrées sans excès de couple.',
    ],
    2004: [
      'Positionnez la pièce verte en respectant les repères d’orientation définis.',
      'Assurez-vous que la pièce est correctement emboîtée avant l’étape suivante.',
      'Vérifiez l’absence de jeu ou de mauvais alignement après la mise en place.',
    ],
    2005: [
      'Pré-engagez les quatre vis afin de faciliter l’alignement des composants.',
      'Veillez à ce que chaque vis soit correctement centrée dans son logement.',
      'Contrôlez visuellement que les quatre vis sont prêtes pour le vissage final.',
    ],
    2006: [
      'Serrez les vis de manière progressive pour répartir uniformément les contraintes.',
      'Respectez le couple de serrage spécifié pour assurer la fiabilité de l’assemblage.',
      'Effectuez un contrôle visuel final pour confirmer le bon serrage de toutes les vis.',
    ],
    // 'cleaning': [
    //   'Wipe surfaces gently to avoid scratching sensitive parts.',
    //   'Remove any debris before cleaning to ensure a smooth finish.',
    //   'Use the appropriate cleaning agent for the material.',
    // ],
  }

  const { socket, subscribeToEvent, unsubscribeFromEvent, sendEvent } = useSocket();
  const { showAlert } = useAlert();

  const { data: patterns } = useGetData('patterns', `patterns`);

  const [selectedPattern, setSelectedPattern] = useState(null);
  const [isStarted, setIsStarted] = useState(false);

  const [stepsList, setStepsList] = useState([]);
  const [currentStep, setCurrentStep] = useState(null);
  const [errorNextStep, setErrorNextStep] = useState(null);
  const [synthesis, setSynthesis] = useState(null);
  const [progress, setProgress] = useState(0);
  const [phases, setPhases] = useState([]);
  const [confidences, setConfidences] = useState([]);
  const [maxValidatedStepIndex, setMaxValidatedStepIndex] = useState(-1);
  const [hasBackwork, setHasBackWork] = useState(false);
  const [systemIsWorking, setSystemIsWorking] = useState(false);
  const [aiMessage, setAiMessage] = useState(null);
  const [alertMessage, setAlertMessage] = useState(null);

  const lockedData = useRef(false);

  const getMissingValidatedSteps = (stepsList) => {
    return stepsList.filter((step) => step.state !== 1);
  };

  const handleStart = () => {
    // if (selectedPattern === null) return; // TODO REMOVE THIS LINE
    if(selectedPattern !== null)
      setIsStarted(true);

    /* Send pattern data to the engine */
    const selectedPatternData = selectedPattern.pattern_event_types
      .sort((pet1, pet2) => pet1.time - pet2.time)
      .map((pet, index) => {
        return {
          index: index,
          subtype: pet.event_type.subtype,
          name: pet.event_type.name,
          content: pet.event_type.content,
          comment: pet.comment,
          state: 0,
          count: 0
        };
      });

    setStepsList(selectedPatternData);

    const stepsToSend = selectedPatternData.map((step) => step.name);
    // console.log(stepsToSend);

    sendEvent('selected_pattern', stepsToSend);
  };

  const resetInterface = () => {
    if (lockedData.current){
      for(let i=0; i<stepsList.length; i++){
        stepsList[i].state = 0;
        stepsList[i].count = 0;
      }
      setStepsList([...stepsList]);
      setCurrentStep(null);
      setErrorNextStep(null);
      setSynthesis(null);
      setProgress(0);
      setConfidences([]);
      setMaxValidatedStepIndex(-1);
      setHasBackWork(false);
      setPhases([]);
      setAiMessage(null);
      setAlertMessage(null);
      lockedData.current = false;
      handleStart();
    }
  };

  useEffect(() => {
    if (!socket) return;

    const handleStepValidated = (stepData) => {
      if (lockedData.current) return;

      console.log('Received validated step:', stepData);
      const type = stepData.type;

      // Get the step corresponding to the received index
      if(!type || stepsList.length === 0 || stepData.index >= stepsList.length){
        console.error('Validated step out of the list.');
        return;
      }

      if(type === 'step'){
        // Check for step error
        // const nextStepToValidate = getNextStepToValidate();
        // if(nextStepToValidate !== null && stepData.index > nextStepToValidate.index){
        //   setErrorNextStep(nextStepToValidate);
        // }
        // else{
        //   setErrorNextStep(null);
        // }

        // Update current step
        if(stepData.state === 2){
          setCurrentStep(stepsList[stepData.index]);

          const stepSubtype = stepsList?.[stepData?.index]?.subtype;
          if (stepSubtype && AI_MESSAGES?.[stepSubtype])
            setAiMessage(AI_MESSAGES[stepSubtype][Math.floor(Math.random() * AI_MESSAGES[stepSubtype].length)]);
          else
            setAiMessage('No available information');
        }
        else {
          setCurrentStep(null);
          setAiMessage(null);
        }

        // Update pattern steps list
        stepsList[stepData.index].state = stepData.state;
        if(stepData.state === 1)
          stepsList[stepData.index].count += 1;
        setStepsList([...stepsList]);

        // Check for backwork
        const newMaxValidatedStepIndex = Math.max(stepData.index, maxValidatedStepIndex);
        setMaxValidatedStepIndex(newMaxValidatedStepIndex);
        if(stepData.index < newMaxValidatedStepIndex)
          setHasBackWork(true);

        // Update progress percentage
        setProgress(Math.floor((stepData.index + 1) / stepsList.length * 100));

        // Check if the pattern is completed
        if(stepData.index === stepsList.length - 1 && stepData.state === 1){
          setTimeout(() => {
            lockedData.current = true;
            setSynthesis({missingSteps: getMissingValidatedSteps(stepsList)});
            // setTimeout(() => {
            //   resetInterface();
            // }, 15000);
          }, 1000);
        }
      }
      else if(type === 'reset'){
        resetInterface();
      }

    };

    const handleReceivedConfidences = (confidences) => {
      if (lockedData.current) return;
      setConfidences(confidences['conf_list']);
    };

    const handleReceivedCurrentPhase = (currentData) => {
      if (lockedData.current || !isStarted) return;

      setAlertMessage(currentData?.alert_message ?? null);
      const currentPhaseType = currentData?.analysis_state;
      if (!currentPhaseType) return;

      // Detect process end
      const eventDate = new Date().getTime() / 1000;

      setPhases(prevPhases => {
        let newPhases = [...prevPhases];

        const firstPhases = [...newPhases];
        const lastPhase = firstPhases.length > 0 ? firstPhases[firstPhases.length - 1] : null;

        // First detected phase or new phase
        if (lastPhase === null || lastPhase.type !== currentPhaseType) {
          newPhases = [...firstPhases, {type: currentPhaseType, start_date: eventDate, end_date: eventDate}];
        }
        // Continuing the same phase
        else {
          newPhases = firstPhases.map((phase, index) =>
            index === firstPhases.length - 1 ? { ...phase, end_date: eventDate } : phase
          );
        }

        return newPhases;
      });
    };

    subscribeToEvent('step_validated', handleStepValidated);
    subscribeToEvent('steps_conf', handleReceivedConfidences)
    subscribeToEvent('current_data', handleReceivedCurrentPhase);

    setSystemIsWorking(true);

    return () => {
      unsubscribeFromEvent('step_validated', handleStepValidated);
      unsubscribeFromEvent('steps_conf', handleReceivedConfidences);
      unsubscribeFromEvent('current_data', handleReceivedCurrentPhase);
    };
  }, [socket, stepsList]);

  const handleRaiseError = () => {
    console.log('New error raised.');
    postData('live_errors', {date: Math.floor(Date.now() / 1000), pattern: `/api/patterns/${selectedPattern?.id}`});
    showAlert('success', 'The error has been reported.')
  };

  const handleCloseSynthesis = (e) => {
    e.stopPropagation();
    resetInterface();
  };

  // TODO TO REMOVE
  // useEffect(() => {
  //   const tmp = async (patterns) => {
  //     if (patterns) {
  //       setSelectedPattern(patterns[5]);
  //       await new Promise(r => setTimeout(r, 100));
  //       // setIsStarted(true);
  //       handleStart();
  //     }
  //   }
  //   tmp(patterns);
  // }, [patterns, selectedPattern]);


  return (
    <div className='h-full overflow-hidden bg-gray-100'>
      {
        alertMessage &&
        <div className="w-full backdrop-blur-md absolute bottom-0 left-0 z-50 bg-perception-error-500/80 flex justify-center items-center
                        text-white text-center transition-all overflow-hidden h-full text-7xl">
          <div className="w-4/5 ">
            {alertMessage}
          </div>
        </div>
      }

      <div className='flex flex-col items-center justify-center w-full h-full'>
        {isStarted && selectedPattern !== null ?
          <div className="w-full h-full flex flex-col gap-4 p-4">

            {/* Header with selected pattern name */}
            <div className="flex gap-4">
              <div className="flex-1 flex items-center gap-4">
                <div className='flex items-center relative group cursor-pointer'>
                  <span className='relative flex'>
                    <span className={`${systemIsWorking ? 'bg-perception-success-300' : 'bg-perception-warn-500'} animate-ping absolute inline-flex h-full w-full rounded-full opacity-75`}/>
                    <span className={`${systemIsWorking ? 'bg-perception-success-300' : 'bg-perception-warn-500'}  relative inline-flex rounded-full h-5 w-5`}/>
                    <span className='absolute left-1/2 -translate-x-1/2 w-max p-2 text-xs text-black bg-white rounded-md hidden group-hover:block'>
                      {systemIsWorking ? 'System connected' : 'Connecting...'}
                    </span>
                  </span>
                </div>

                <p className="text-3xl">
                  {selectedPattern.name}
                </p>
              </div>

              <div className="h-full flex gap-4">
                {/* Logos */}
                <div className="pb-0 flex justify-center items-center gap-4 relative">
                  <img src={LogoPerception} alt="logo-perception" className="max-w-[200px] max-h-[25px]" />
                  {/*<span className="text-xl">x</span>*/}
                  {/*<img src={LogoClient} alt="logo-perception" className="max-w-[200px] max-h-[25px]" />*/}
                </div>

                {SHOW_RESTART_BUTTON ?
                  <ActionButton color="secondary" size="md" Icon={ArrowPathIcon} onClick={() => {lockedData.current = true; resetInterface()}}/> :
                  <ActionButton text="Go to activity log" color="secondary" size="md" Icon={BookOpenIcon} onClick={() => window.location = '/journal'}/>
                }
                <ActionButton color="secondary" size="md" Icon={XMarkIcon} onClick={() => window.location.reload()}/>

                {/* Report an error */}
                <ActionButton text="Reporter une erreur" color="danger" size="md" onClick={handleRaiseError}/>
              </div>

            </div>


            {/* Completed steps */}
            <div className="flex flex-1 min-h-0 gap-4">
              <div className="w-1/2 min-h-0 flex flex-col gap-4 bg-gray-200 p-4 rounded-md overflow-auto">
                <p className="text-2xl">Liste des opérations :</p>
                {
                  stepsList.map((step, stepIndex) => {
                    const firstNonValidatedStepIndex = stepsList.findIndex((currentStep) => currentStep.state !== 1);
                    const isFirstNonValidatedStep = stepIndex === firstNonValidatedStepIndex;
                    const hasFutureValidatedStep = stepsList.some((futureStep, futureStepIndex) =>
                      futureStepIndex > stepIndex && futureStep.state >= 1
                    );

                    let textColor = 'text-black';
                    let bgColor = 'bg-gray-100';
                    let stateText = null;

                    if (step.state === 1 && step.count === 1) {
                      textColor = 'text-white';
                      bgColor = 'bg-perception-success-600';
                      stateText = 'Validée';
                    }
                    else if (step.state === 1 && step.count > 1) {
                      textColor = 'text-white';
                      bgColor = 'bg-perception-error-500';
                      stateText = `Validée (${step.count} fois)`;
                    }
                    else if (step.state !== 1 && hasFutureValidatedStep) {
                      textColor = 'text-white';
                      bgColor = 'bg-perception-error-500';
                      stateText = 'Non validée';
                    }
                    else if (step.state === 2) {
                      bgColor = 'bg-yellow-500';
                      stateText = 'En cours';
                    }
                    else if (isFirstNonValidatedStep) {
                      bgColor = 'bg-yellow-500';
                      stateText = 'En attente';
                    }

                    return (
                      <div key={stepIndex} className={`flex shrink-0 gap-4 items-center p-4 rounded-md text-2xl ${step.state === 1 ? 'bg-perception-gray-500' : 'bg-white'}`}>
                        <Badge text={stepIndex + 1} type={`${step.state === 1 ? 'black' : 'blue'}`}/>
                        {step.content}
                        {stateText &&
                          <span className={`${textColor} ${bgColor} px-2 py-1 rounded-md text-xl text-white`}>{stateText}</span>
                        }
                      </div>
                    )
                  })
                }
              </div>

              <div className="h-full min-h-0 w-1/2 flex flex-col gap-4">
                {/* Current step */}
                <div className="bg-gray-200 p-4 text-center h-full flex flex-col justify-center items-center rounded-md">
                  <p className="text-2xl">Etape en cours :</p>
                  <p className="text-5xl">
                    {
                      currentStep ? <span className="text-perception-blue">{currentStep.content}</span> : 'Aucune'
                    }
                  </p>
                </div>

                {
                  aiMessage &&
                    <div className="bg-gray-200 p-4 text-center h-full flex flex-col justify-center items-center rounded-md text-5xl">
                      <p className="text-2xl">Instructions :</p>
                      {aiMessage}
                    </div>
                }
              </div>
            </div>


            <div className="flex flex-col h-[20%] shrink-0">
              {/* Analysis animation / Progress */}
              <GeneralTimeline
                phases={phases}
                phasesColor={['bg-gray-500', 'bg-green-500', 'bg-perception-blue', 'bg-pink-500', 'bg-yellow-500']}
                phasesText={phasesText}
                bgColor="bg-gray-200" tickColor="dark"
              />
            </div>
          </div>
          :
          <>
            <h2 className='my-6 text-3xl font-semibold'>Select a manufacturing process</h2>
            <div className='flex flex-col gap-4 w-80 '>
              <InputSelect
                className='py-3'
                items={patterns}
                onChange={(pattern) => setSelectedPattern(pattern)}
                itemToString={(pattern) => pattern?.name}
                placeholder='Select a manufacturing process'
                category='secondary'
                autoComplete='off'
              />
            </div>
            <div className='my-6'>
              <Button type='submit' category='primary-btn' size='small' onClick={handleStart}>
                Start
              </Button>
            </div>
          </>
      }
    </div>

      {
        synthesis &&
        <div className="absolute top-0 left-0 w-full h-full p-20 bg-black/50 backdrop-blur-sm">
          <div className="relative bg-white h-full rounded-md flex flex-col justify-between p-8">
            <div className="absolute top-8 right-8 cursor-pointer" onClick={(e) => handleCloseSynthesis(e)}>
              <XMarkIcon className="w-16 h-16 text-gray-600 m-4"/>
            </div>
            <h1 className="bg-gray-200 text-center text-3xl p-8 rounded-md">Synthèse de la production</h1>
            <div className="h-full flex justify-center items-center overflow-auto">
              <div className="w-full h-full flex flex-col justify-center items-center text-3xl">
                {
                  synthesis?.missingSteps?.length === 0 && !hasBackwork ?
                    <>
                      <CheckIcon className="w-48 h-48 text-perception-success-300"/>
                      <p>Aucun problème détecté</p>
                    </>
                    :
                    <div className="flex flex-col justify-around items-center h-full">
                      <div className="flex flex-col items-center">
                        <ExclamationCircleIcon className="w-36 h-36 text-perception-warn-500"/>
                        <p className="text-center">Vérification nécessaire pour certaines étapes</p>
                      </div>
                      <div className="flex flex-col items-start gap-4">
                        {
                          hasBackwork ?
                            <div className="flex rounded-md bg-yellow-50 p-4">
                              <div className="shrink-0">
                                <ExclamationTriangleIcon aria-hidden="true" className="size-5 text-yellow-400" />
                              </div>
                              <div className="ml-3">
                                <h3 className="text-sm font-medium text-yellow-800">Un retour en arrière a été nécessaire</h3>
                                <div className="mt-2 text-sm text-yellow-700">
                                  <p>Certaines opérations ont dû être réalisées plusieurs fois en réponse à un problème détecté.</p>
                                </div>
                              </div>
                            </div>
                            :
                            <div className="flex rounded-md bg-green-50 p-4">
                              <div className="shrink-0">
                                <CheckCircleIcon aria-hidden="true" className="size-5 text-green-400" />
                              </div>
                              <div className="ml-3">
                                <h3 className="text-sm font-medium text-green-800">Pas de retour arrière détecté</h3>
                                <div className="mt-2 text-sm text-green-700">
                                  <p>Aucune opération déjà réalisée n'a nécessité de vérification.</p>
                                </div>
                              </div>
                            </div>
                        }
                      </div>
                    </div>
                }
              </div>
              <div className="w-full flex flex-col gap-4 bg-gray-200 p-4 overflow-auto rounded-md">
                {
                  stepsList.map((step, index) => {
                    let textColor = 'text-black';
                    let bgColor = 'bg-gray-100';
                    let stateText = null;

                    if (step.state === 1 && step.count === 1) {
                      textColor = 'text-white';
                      bgColor = 'bg-perception-success-600';
                      stateText = 'Etape validée';
                    }
                    else if (step.state === 1 && step.count > 1) {
                      textColor = 'text-white';
                      bgColor = 'bg-perception-warn-500';
                      stateText = `Vérification nécessaire (Validée ${step.count} fois)`;
                    }
                    else if (index < confidences.length && confidences[index] > 0.3) {
                      textColor = 'text-white';
                      bgColor = 'bg-perception-warn-500';
                      stateText = `Vérification nécessaire`;
                    }
                    else {
                      textColor = 'text-white';
                      bgColor = 'bg-perception-error-500';
                      stateText = 'Vérification nécessaire (fortement recommandée)';
                    }

                    return (
                      <div key={index} className={`p-4 rounded-md ${textColor} ${bgColor} text-2xl `}>
                        {/*<span className="bg-black/50 px-2 py-1 mr-4 rounded-md">{operation.id}</span>*/}
                        {step.content}
                        {
                          stateText &&
                          <span className="bg-black/30 px-2 py-1 rounded-md text-xl text-white ml-4">{stateText}</span>
                        }
                      </div>
                    )
                  })
                }
              </div>
            </div>
            <div className="flex justify-center bg-gray-200 p-4 rounded-md">
              <button className="bg-error text-white p-4 rounded-md" onClick={handleRaiseError}>Reporter une erreur</button>
            </div>
          </div>
        </div>
      }
    </div>
  );
};
